import { useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useQuickstartFlow } from '../../features/quickstart/useQuickstartFlow';
import { QuickstartPhase } from '../../features/quickstart/QuickstartController';
import { EmbeddedBrowserRedirect } from '../../features/quickstart/components/EmbeddedBrowserRedirect';
import { QuickstartLoading } from '../../features/quickstart/components/QuickstartLoading';
import { QuickstartError } from '../../features/quickstart/components/QuickstartError';
import { QuickstartCTA } from '../../features/quickstart/components/QuickstartCTA';
import { QuickstartThankYouRedirect } from '../../features/quickstart/components/QuickstartThankYouRedirect';
import { QuickstartMobileThankYou } from '../../features/quickstart/components/QuickstartMobileThankYou';
import { QuickstartAuthWrapper } from '../../features/quickstart/components/QuickstartAuthWrapper';
import { PlayerWithQuickstart } from '../player/PlayerWithQuickstart';
import { QuickstartDebugOverlay } from '../../features/quickstart/components/QuickstartDebugPage';
import { useIsMobileDevice } from '../../utility/utility';

/**
 * Map phases to component keys to prevent same component from animating to itself
 * Only animate when the actual component type changes
 */
function getComponentKey(phase: QuickstartPhase): string {
  switch (phase) {
    case 'page_viewed':
    case 'environment_check':
    case 'creating_lobby':
    case 'matchmaking':
    case 'match_found':
    case 'joining_broadcast':
      return 'loading';

    case 'embedded_browser_blocked':
      return 'embedded-browser';

    case 'playing_afk':
    case 'playing_active':
      return 'player';

    case 'afk_timeout':
    case 'ad_timer_expired':
    case 'wishlist_early_exit':
    case 'thank_you':
      return 'thank-you';

    case 'matchmaking_failed':
    case 'broadcast_join_failed':
    case 'error':
    case 'auth_failed':
      return 'error';

    case 'cta_page':
      return 'cta';

    case 'auth_attempted':
      return 'auth';

    default:
      return phase;
  }
}

export const Quickstart = () => {
  const { quickstart } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasProcessedNestedParamsRef = useRef(false);
  const isMobile = useIsMobileDevice();

  // Check for debug mode and handle nested params
  const isDebugMode = searchParams.has('debug');
  const debugPhaseParam = searchParams.get('debug');

  // Handle nested query params in debug parameter (e.g., ?debug=?utm_source=discord or ?debug=utm_source=discord)
  // If debug value contains '=' or starts with '?', parse it as additional query params
  useEffect(() => {
    if (!hasProcessedNestedParamsRef.current && debugPhaseParam && (debugPhaseParam.includes('=') || debugPhaseParam.startsWith('?'))) {
      hasProcessedNestedParamsRef.current = true;

      const decodedDebug = decodeURIComponent(debugPhaseParam);
      console.log('[Quickstart] Detected nested params in debug:', decodedDebug);

      // Parse the nested params
      const cleanParamString = decodedDebug.startsWith('?') ? decodedDebug.slice(1) : decodedDebug;
      const nestedParams = new URLSearchParams(cleanParamString);

      // Create new params with nested values merged in
      const newParams = new URLSearchParams(searchParams);
      nestedParams.forEach((value, key) => {
        newParams.set(key, value);
      });

      // Set debug to empty string to keep debug mode but not treat it as a phase
      newParams.set('debug', '');

      console.log('[Quickstart] Rewriting URL with expanded params:', newParams.toString());

      // Update URL
      setSearchParams(newParams, { replace: true });
    }
  }, [debugPhaseParam, searchParams, setSearchParams]);

  const debugPhase =
    debugPhaseParam && debugPhaseParam !== '' && !debugPhaseParam.includes('=') && !debugPhaseParam.startsWith('?')
      ? (debugPhaseParam as QuickstartPhase)
      : undefined;

  const {
    state,
    controller,
    onFirstInput,
    onWishlistClicked,
    onWishlistCTAClicked,
    onThankYouRedirect,
    onOpenInBrowserClicked,
    onAuthSuccess,
    onAuthFailure,
    requestBrowserLocks,
    onAfkTimeout,
    onAdTimerExpired,
    onPlayerDisconnect,
    getAFKRemainingTime,
    getAdRemainingTime,
    debugJumpToPhase,
    debugSetReleaseTimestamp,
  } = useQuickstartFlow(quickstart || 'test', debugPhase);

  if (!state) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <div className='h-12 w-12 animate-spin rounded-full border-4 border-white border-t-transparent' />
      </div>
    );
  }

  // Render debug overlay if in debug mode
  const debugOverlay = isDebugMode ? (
    <QuickstartDebugOverlay
      currentPhase={state.phase}
      currentTheme={state.theme}
      campaignId={quickstart || 'test'}
      onSelectPhase={debugJumpToPhase}
      onSetReleaseTimestamp={debugSetReleaseTimestamp}
    />
  ) : null;

  // Render content based on phase
  const renderPhaseContent = () => {
    switch (state.phase) {
      case 'page_viewed':
      case 'environment_check':
        return (
          <>
            <QuickstartLoading theme={state.theme} message='Loading...' />
            {debugOverlay}
          </>
        );

      case 'embedded_browser_blocked':
        return (
          <>
            <EmbeddedBrowserRedirect theme={state.theme} environment={state.environment} onOpenInBrowserClicked={onOpenInBrowserClicked} />
            {debugOverlay}
          </>
        );

      case 'creating_lobby':
      case 'matchmaking':
        return (
          <>
            <QuickstartLoading theme={state.theme} />
            {debugOverlay}
          </>
        );

      case 'matchmaking_failed':
      case 'broadcast_join_failed':
      case 'error':
        return (
          <>
            <QuickstartThankYouRedirect
              theme={state.theme}
              autoRedirect={state.shouldAutoRedirectToStore}
              onRedirect={() => onWishlistCTAClicked()}
            />
            {debugOverlay}
          </>
        );

      case 'match_found':
      case 'joining_broadcast':
        return (
          <>
            <QuickstartLoading theme={state.theme} message='Connecting to game...' />
            {debugOverlay}
          </>
        );

      case 'playing_afk':
      case 'playing_active':
        return (
          <>
            <PlayerWithQuickstart
              quickstartMode={true}
              afkTimerActive={state.afkTimerActive}
              adTimerActive={state.adTimerActive}
              adTimerSeconds={state.adTimerSeconds}
              afkTimeoutSeconds={state.afkTimeoutSeconds}
              shouldShowPlayNowOverlay={state.shouldShowPlayNowOverlay}
              theme={state.theme}
              access={state.access}
              onFirstInput={onFirstInput}
              requestBrowserLocks={requestBrowserLocks}
              onWishlistClicked={onWishlistClicked}
              onAfkTimeout={onAfkTimeout}
              onAdTimerExpired={onAdTimerExpired}
              onDisconnect={onPlayerDisconnect}
              isDebugMode={isDebugMode}
              controller={controller}
              getAFKRemainingTime={getAFKRemainingTime}
              getAdRemainingTime={getAdRemainingTime}
            />
            {debugOverlay}
          </>
        );

      case 'afk_timeout':
      case 'ad_timer_expired':
      case 'wishlist_early_exit':
      case 'thank_you':
        return (
          <>
            {isMobile && state.theme.requireAuth ? (
              <QuickstartMobileThankYou
                theme={state.theme}
                autoRedirect={state.shouldAutoRedirectToStore}
                onRedirect={() => onThankYouRedirect(state.theme.steamUrl || '')}
              />
            ) : (
            <QuickstartThankYouRedirect
              theme={state.theme}
              autoRedirect={state.shouldAutoRedirectToStore}
                onRedirect={() => onThankYouRedirect(state.theme.steamUrl || '')}
            />
            )}
            {debugOverlay}
          </>
        );

      case 'cta_page':
        return (
          <>
            <QuickstartCTA theme={state.theme} exitReason={state.exitReason} onWishlistCTAClicked={() => onWishlistCTAClicked()} />
            {debugOverlay}
          </>
        );

      case 'auth_attempted':
        return (
          <>
            <QuickstartAuthWrapper
              theme={state.theme}
              authProvider={state.authProvider || 'google'}
              onAuthSuccess={onAuthSuccess}
              onAuthFailure={onAuthFailure}
              onClerkUnavailable={() => controller?.onClerkUnavailable()}
            />
            {debugOverlay}
          </>
        );

      case 'auth_failed':
        return (
          <>
            <QuickstartThankYouRedirect
              theme={state.theme}
              autoRedirect={state.shouldAutoRedirectToStore}
              onRedirect={() => onWishlistCTAClicked()}
            />
            {debugOverlay}
          </>
        );

      default:
        return (
          <>
            <QuickstartError theme={state.theme} message='Unknown state' onWishlistCTAClicked={onWishlistCTAClicked} />
            {debugOverlay}
          </>
        );
    }
  };

  // Use component-based key to prevent same component from animating to itself
  const componentKey = getComponentKey(state.phase);

  return (
    <AnimatePresence mode='wait'>
      <div key={componentKey}>{renderPhaseContent()}</div>
    </AnimatePresence>
  );
};
